/* HopeBadge
 * ─────────────────────────────────────────────────────────────────
 * Pill-style "HOPE" badge for athletes tagged into the Hope series.
 * Modelled on the Emmy chip used on Mike's TeamSection card — a rounded
 * pill anchored to the bottom-right of the athlete's network avatar.
 *
 * Brand colors (per the Hope logo art): black fill, white display text,
 * cyan (#22D3EE) inner ring + pink (#EC4899) outer ring stacked via
 * box-shadow so the badge keeps its sticker silhouette at any size.
 *
 * Tapping the badge routes to /brand/hope — the dedicated Hope brand
 * landing page (mission, videos, athletes, stories, products).
 *
 * Variants:
 *   pill      — default. `absolute -bottom-1 -right-1` pill on its
 *               nearest `relative` parent (mirrors the Emmy badge).
 *   inline    — render inline next to a name / heading.
 *   absolute  — legacy alias for `pill`.
 */
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

const SIZE_MAP = {
  sm: { pad: 'px-1.5 py-[1px]', text: 'text-[9px]',  icon: 'w-2.5 h-2.5' },
  md: { pad: 'px-2 py-0.5',     text: 'text-[10px]', icon: 'w-3 h-3' },
  lg: { pad: 'px-2.5 py-1',     text: 'text-[12px]', icon: 'w-3.5 h-3.5' },
};

const HOPE_RING = {
  boxShadow:
    'inset 0 0 0 1.5px #22D3EE, 0 0 0 1.5px #EC4899, 0 6px 16px -6px rgba(236,72,153,0.55)',
};

const HOPE_HREF = '/brand/hope';

const HopeBadge = ({ variant = 'pill', size = 'md', className = '' }) => {
  const s = SIZE_MAP[size] || SIZE_MAP.md;
  const navigate = useNavigate();
  const core = `inline-flex items-center gap-1 ${s.pad} ${s.text} font-display tracking-[0.18em] uppercase text-white bg-black rounded-full select-none transition-transform hover:scale-105 active:scale-95 cursor-pointer`;

  const inner = (
    <>
      <Sparkles className={`${s.icon} text-cyan-300`} />
      HOPE
    </>
  );

  // iter235 · Rendered as a span (role=link) instead of <Link> — the
  // badge often sits INSIDE an athlete-card <Link>, and nested anchors
  // are invalid DOM (React warns + click behavior gets ambiguous).
  // We swallow the click so the parent card doesn't also navigate.
  const go = (e) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(HOPE_HREF);
  };
  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') go(e);
  };

  if (variant === 'inline') {
    return (
      <span
        role="link"
        tabIndex={0}
        onClick={go}
        onKeyDown={onKey}
        data-testid="hope-badge"
        className={`${core} ${className}`}
        style={HOPE_RING}
        title="Open the Hope brand page"
      >
        {inner}
      </span>
    );
  }

  return (
    <span
      role="link"
      tabIndex={0}
      onClick={go}
      onKeyDown={onKey}
      data-testid="hope-badge"
      className={`absolute -bottom-1 -right-1 z-10 ${core} shadow-md ${className}`}
      style={HOPE_RING}
      title="Open the Hope brand page"
    >
      {inner}
    </span>
  );
};

export default HopeBadge;
